import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Paper,
  TextField,
  InputAdornment,
  Skeleton,
} from '@mui/material';
import {
  Search as SearchIcon,
  TrendingUp,
  Inventory,
  Security,
} from '@mui/icons-material';
import { partsService } from '../services/partsService';
import ProductCard from '../components/product/ProductCard';

const features = [
  {
    icon: <Inventory />,
    title: '10M+ Components',
    description: 'Largest inventory of electronic components from trusted manufacturers',
  },
  {
    icon: <Security />,
    title: 'Authorized Distributor',
    description: '100% authentic components with manufacturer warranty',
  },
  {
    icon: <TrendingUp />,
    title: 'Real-time Pricing',
    description: 'Live inventory and competitive pricing updates',
  },
];

const HomePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Query for featured products
  const { data: products, isLoading: loading } = useQuery({
    queryKey: ['featuredProducts'],
    queryFn: () => partsService.getFeaturedParts(8),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/parts?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handlePartClick = (part) => {
    navigate(`/parts/${part.componentId || part.id}/${encodeURIComponent(part.partNumber)}`, {
      state: { part }
    });
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h2" component="h1" gutterBottom fontWeight="bold">
                Find Electronic Components Fast
              </Typography>
              <Typography variant="h5" gutterBottom sx={{ opacity: 0.9 }}>
                Search millions of parts from top manufacturers.
                Get what you need, when you need it.
              </Typography>

              <Box sx={{ mt: 4, maxWidth: 500 }}>
                <form onSubmit={handleSearch}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by part number, description, or manufacturer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      bgcolor: 'white',
                      borderRadius: 1,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            type="submit"
                            variant="contained"
                            sx={{ mr: -1 }}
                          >
                            Search
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </form>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{
                width: '100%', height: 320, borderRadius: 2,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              }}>
                <Inventory sx={{ fontSize: 96, color: 'rgba(255,255,255,0.6)' }} />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: 6, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
            Why Choose PartzFlow?
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, mt: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
            {features.map((feature, index) => (
              <Paper
                key={index}
                elevation={2}
                sx={{
                  p: 3,
                  textAlign: 'center',
                  flex: '1 1 calc(33.333% - 24px)',
                  minWidth: '280px',
                  maxWidth: '400px',
                  minHeight: 220,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>
                  {React.cloneElement(feature.icon, { sx: { fontSize: 48 } })}
                </Box>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Featured Products Section */}
      <Box sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Typography variant="h4" fontWeight="bold">
              Featured Products
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/parts')}
            >
              View All Products
            </Button>
          </Box>

          <Grid container spacing={3}>
            {loading ? (
              // Loading skeletons
              Array.from({ length: 8 }).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card sx={{ height: '100%' }}>
                    <Skeleton variant="rectangular" height={200} />
                    <CardContent>
                      <Skeleton variant="text" height={20} sx={{ mb: 1 }} />
                      <Skeleton variant="text" height={28} />
                      <Skeleton variant="text" height={20} />
                      <Skeleton variant="text" height={20} />
                      <Skeleton variant="text" height={32} />
                    </CardContent>
                    <CardActions sx={{ p: 2 }}>
                      <Skeleton variant="rectangular" width="100%" height={36} />
                    </CardActions>
                  </Card>
                </Grid>
              ))
            ) : !products || products.length === 0 ? (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Inventory sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="h6" color="textSecondary">
                    No Products Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Featured products will appear here once the parts database is populated.
                  </Typography>
                </Paper>
              </Grid>
            ) : (
              products.map((product) => (
                <Grid item xs={12} sm={6} md={3} key={product.id}>
                  <ProductCard product={product} onPartClick={handlePartClick} />
                </Grid>
              ))
            )}
          </Grid>
        </Container>
      </Box>

      {/* Newsletter Section */}
      <Box sx={{ py: 6, bgcolor: 'primary.main', color: 'white' }}>
        <Container maxWidth="md">
          <Typography variant="h4" align="center" gutterBottom fontWeight="bold">
            Stay Updated
          </Typography>
          <Typography variant="h6" align="center" gutterBottom sx={{ opacity: 0.9 }}>
            Get the latest product updates and special offers
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 4, maxWidth: 500, mx: 'auto' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your email"
              sx={{
                bgcolor: 'white',
                borderRadius: 1,
              }}
            />
            <Button variant="contained" size="large" sx={{ bgcolor: 'secondary.main' }}>
              Subscribe
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;